import React, { useState, useEffect } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, message } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SanthaMaster = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Generate financial year options (previous 3 years, current year, and next year)
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0 = January, 3 = April, etc.

    // Determine the current financial year
    const currentFinancialYearStart = currentMonth >= 3 ? currentYear : currentYear - 1;
    const years = [];

    // Add previous 3 financial years, current financial year, and next financial year
    for (let i = currentFinancialYearStart - 3; i <= currentFinancialYearStart + 1; i++) {
      years.push({
        value: `${i}-${i + 1}`,
        label: `${i}-${i + 1}`,
      });
    }

    return years;
  };

  // Get the current financial year
  const getCurrentFinancialYear = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentFinancialYearStart = currentMonth >= 3 ? currentYear : currentYear - 1;
    return `${currentFinancialYearStart}-${currentFinancialYearStart + 1}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get("/member/getsanthamaster");
      setData(response.data.data || []);
    } catch (error) {
      message.error("Failed to fetch santhas.");
    }
  };

  const handleAdd = async (values) => {
    try {
      await api.post("/member/createsanthamaster", values);
      message.success("Santha added successfully");
      fetchData();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to add santha.");
    }
  };

  const handleUpdate = async (values) => {
    try {
      await api.put(`/member/upsanthamaster/${currentRecord._id}`, values);
      message.success("Santha updated successfully");
      fetchData();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to update santha.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/member/delsanthamaster/${id}`);
      message.success("Santha deleted successfully");
      fetchData();
    } catch (error) {
      message.error("Failed to delete santha.");
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Santha Data", 10, 10);

    const tableColumn = ["S.No", "Santha Name", "Description", "Financial Year", "Status"];
    const tableRows = data.map((item, index) => [
      index + 1,
      item.santhaname,
      item.desc,
      item.financialYear,
      item.status,
    ]);

    doc.autoTable({ head: [tableColumn], body: tableRows });
    doc.save("santha_data.pdf");
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item, index) => ({
        "S.No": index + 1,
        "Santha Name": item.santhaname,
        "Description": item.desc,
        "Financial Year": item.financialYear,
        "Status": item.status,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Santhas");
    XLSX.writeFile(workbook, "santha_data.xlsx");
  };

  const filteredData = data.filter((record) =>
    record.santhaname.toLowerCase().includes(searchText.toLowerCase()) ||
    record.desc.toLowerCase().includes(searchText.toLowerCase()) ||
    (record.financialYear && record.financialYear.toLowerCase().includes(searchText.toLowerCase())) ||
    record.status.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    { title: "S.No", render: (_, __, index) => index + 1 },
    { title: "Santha Name", dataIndex: "santhaname" },
    { title: "Description", dataIndex: "desc" },
    { title: "Financial Year", dataIndex: "financialYear" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditMode(true);
              setCurrentRecord(record);
              form.setFieldsValue(record);
              setIsModalVisible(true);
            }}
          />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row>
        <Col span={6}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search Santha"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <Button icon={<FileExcelOutlined />} onClick={exportToExcel} style={{ marginRight: 8 }}>Export Excel</Button>
          <Button icon={<FilePdfOutlined />} onClick={exportToPDF} style={{ marginRight: 8 }}>Export PDF</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditMode(false);
              form.resetFields();
              form.setFieldsValue({ financialYear: getCurrentFinancialYear() }); // Set current financial year
              setIsModalVisible(true);
            }}
          >
            Add Santha
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={filteredData} rowKey="_id" />
      <Modal
        title={editMode ? "Edit Santha" : "Add Santha"}
        open={isModalVisible}
        onOk={() => form.validateFields().then((values) => (editMode ? handleUpdate(values) : handleAdd(values)))}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Form.Item label="Santha Name" name="santhaname" rules={[{ required: true }]}>
            <Input placeholder="Enter Santha Name" />
          </Form.Item>
          <Form.Item label="Financial Year" name="financialYear" rules={[{ required: true }]}>
            <Select options={generateFinancialYears()} placeholder="Select Financial Year" />
          </Form.Item>
          <Form.Item label="Description" name="desc" rules={[{ required: true }]}>
            <Input placeholder="Enter Description" />
          </Form.Item>        
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }]} placeholder="Select Status" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SanthaMaster;